import React from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  GUESTERIA_PARTNER_CATEGORY_OPTIONS,
  GUESTERIA_PARTNER_CONNECTION_OPTIONS,
  countActiveGuesteriaFilters,
  DEFAULT_GUESTERIA_PARTNERS_FILTERS,
  type GuesteriaPartnersFiltersState,
} from "@/components/pages/guesteria-partners/utils/guesteriaPartnersFilters";
import { GuesteriaPartnersFilterButton } from "@/components/pages/guesteria-partners/components/GuesteriaPartnersFilterButton";
import type {
  GuesteriaPartnerCategoryId,
  GuesteriaPartnerConnectionStatus,
} from "@/components/pages/guesteria-partners/utils/guesteriaPartnersModel";
import { cn } from "@/lib/utils";

const GUESTERIA_FILTER_CHECKBOX_CLASS =
  "size-5 rounded-[4px] border-[#D5D7DA] data-[state=checked]:border-[#30344A] data-[state=checked]:bg-[#30344A] data-[state=checked]:text-white";

const GUESTERIA_FILTER_COMBOBOX_TRIGGER_CLASS =
  "flex h-9 w-full items-center justify-between gap-2 rounded-md border border-[#E5E5E5] bg-white px-4 py-2 text-left text-sm font-medium leading-normal text-[#737373] outline-none transition-colors hover:bg-zinc-50/80 focus-visible:ring-0";

export type GuesteriaPartnersFiltersPopoverProps = {
  applied: GuesteriaPartnersFiltersState;
  onApply: (next: GuesteriaPartnersFiltersState) => void;
};

export function GuesteriaPartnersFiltersPopover({
  applied,
  onApply,
}: GuesteriaPartnersFiltersPopoverProps): React.JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] =
    React.useState<GuesteriaPartnersFiltersState>(applied);
  const [categoryOpen, setCategoryOpen] = React.useState(true);
  const [categorySearch, setCategorySearch] = React.useState("");
  const [connectionOpen, setConnectionOpen] = React.useState(false);

  const activeCount = countActiveGuesteriaFilters(draft);

  const visibleCategoryOptions = React.useMemo(() => {
    const q = categorySearch.trim().toLowerCase();
    if (!q) {
      return GUESTERIA_PARTNER_CATEGORY_OPTIONS;
    }
    return GUESTERIA_PARTNER_CATEGORY_OPTIONS.filter((category) =>
      t(`partners.table.categoryValues.${category}`).toLowerCase().includes(q),
    );
  }, [categorySearch, t]);

  const syncDraftFromApplied = React.useCallback(() => {
    setDraft(applied);
    setCategorySearch("");
    setCategoryOpen(true);
    setConnectionOpen(applied.connectionStatuses.length > 0);
  }, [applied]);

  const toggleCategory = (category: GuesteriaPartnerCategoryId) => {
    setDraft((prev) => {
      const has = prev.categories.includes(category);
      const categories = has
        ? prev.categories.filter((c) => c !== category)
        : [...prev.categories, category];
      return { ...prev, categories };
    });
  };

  const removeCategoryChip = (category: GuesteriaPartnerCategoryId) => {
    setDraft((prev) => ({
      ...prev,
      categories: prev.categories.filter((c) => c !== category),
    }));
  };

  const toggleConnection = (status: GuesteriaPartnerConnectionStatus) => {
    setDraft((prev) => {
      const has = prev.connectionStatuses.includes(status);
      const connectionStatuses = has
        ? prev.connectionStatuses.filter((s) => s !== status)
        : [...prev.connectionStatuses, status];
      return { ...prev, connectionStatuses };
    });
  };

  const removeConnectionChip = (status: GuesteriaPartnerConnectionStatus) => {
    setDraft((prev) => ({
      ...prev,
      connectionStatuses: prev.connectionStatuses.filter((s) => s !== status),
    }));
  };

  const clearSearch = () => {
    setDraft((prev) => ({ ...prev, searchQuery: "" }));
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) {
          syncDraftFromApplied();
        }
      }}
    >
      <PopoverTrigger asChild>
        <GuesteriaPartnersFilterButton />
      </PopoverTrigger>

      <PopoverContent
        align="start"
        sideOffset={8}
        className="flex max-h-[min(560px,calc(100vh-120px))] w-[320px] flex-col gap-6 overflow-hidden rounded-2xl border border-[#E5E5E5] bg-white p-4 shadow-[0px_3px_3px_0px_rgba(0,0,0,0.12)]"
      >
        <div className="flex shrink-0 flex-col gap-3">
          <div className="flex items-start justify-between gap-2">
            <h4 className="text-base leading-normal font-semibold text-[#171717]">
              {activeCount > 0
                ? t("partners.table.filterPopover.titleWithCount", {
                    count: activeCount,
                  })
                : t("partners.table.filterPopover.title")}
            </h4>
            <Button
              type="button"
              variant="span"
              className="rounded p-1 text-zinc-500 transition hover:bg-zinc-100 hover:text-zinc-900"
              onClick={() => setOpen(false)}
              aria-label={t("partners.table.filterPopover.closeAria")}
            >
              <X className="size-5" />
            </Button>
          </div>

          {activeCount > 0 ? (
            <div className="flex flex-wrap gap-2">
              {draft.searchQuery.trim() ? (
                <span className="inline-flex max-w-full items-center gap-1 rounded-md bg-[#EDE9FE] px-2 py-1 text-xs font-medium text-[#5B21B6]">
                  <span className="truncate">{draft.searchQuery.trim()}</span>
                  <Button
                    type="button"
                    variant="span"
                    className="rounded p-0.5 hover:bg-[#DDD6FE]"
                    onClick={clearSearch}
                    aria-label={t(
                      "partners.table.filterPopover.clearSearchAria",
                    )}
                  >
                    <X className="size-3.5 shrink-0" />
                  </Button>
                </span>
              ) : null}
              {draft.categories.map((category) => (
                <span
                  key={category}
                  className="inline-flex items-center gap-1 rounded-md bg-[#EDE9FE] px-2 py-1 text-xs font-medium text-[#5B21B6]"
                >
                  {t(`partners.table.categoryValues.${category}`)}
                  <Button
                    type="button"
                    variant="span"
                    className="rounded p-0.5 hover:bg-[#DDD6FE]"
                    onClick={() => removeCategoryChip(category)}
                    aria-label={t("partners.table.filterPopover.closeAria")}
                  >
                    <X className="size-3.5 shrink-0" />
                  </Button>
                </span>
              ))}
              {draft.connectionStatuses.map((status) => (
                <span
                  key={status}
                  className="inline-flex items-center gap-1 rounded-md bg-[#EDE9FE] px-2 py-1 text-xs font-medium text-[#5B21B6]"
                >
                  {t(`partners.table.connectionStatusValues.${status}`)}
                  <Button
                    type="button"
                    variant="span"
                    className="rounded p-0.5 hover:bg-[#DDD6FE]"
                    onClick={() => removeConnectionChip(status)}
                    aria-label={t("partners.table.filterPopover.closeAria")}
                  >
                    <X className="size-3.5 shrink-0" />
                  </Button>
                </span>
              ))}
            </div>
          ) : null}
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto pr-0.5">
          <div className="relative shrink-0">
            <Input
              type="search"
              icon={<Search className="size-4 text-[#737373]" />}
              placeholder={t("partners.table.filterPopover.searchPlaceholder")}
              value={draft.searchQuery}
              onChange={(e) =>
                setDraft((prev) => ({ ...prev, searchQuery: e.target.value }))
              }
              className="h-9 border-[#E5E5E5] pr-9 text-sm text-[#171717] shadow-none placeholder:text-[#737373] focus-visible:border-[#E5E5E5] focus-visible:ring-0"
            />
            {draft.searchQuery ? (
              <Button
                type="button"
                variant="span"
                className="absolute top-1/2 right-2 -translate-y-1/2 rounded p-1 text-[#737373] hover:bg-zinc-100 hover:text-[#171717]"
                onClick={clearSearch}
                aria-label={t("partners.table.filterPopover.clearSearchAria")}
              >
                <X className="size-4" />
              </Button>
            ) : null}
          </div>

          <div className="space-y-2">
            <Button
              type="button"
              variant="span"
              className={GUESTERIA_FILTER_COMBOBOX_TRIGGER_CLASS}
              onClick={() => setCategoryOpen((v) => !v)}
              aria-expanded={categoryOpen}
            >
              {t("partners.table.filterPopover.category")}
              <ChevronDown
                className={cn(
                  "size-4 shrink-0 text-[#737373] transition-transform",
                  categoryOpen && "rotate-180",
                )}
              />
            </Button>

            {categoryOpen ? (
              <div className="overflow-hidden rounded-md border border-[#E5E5E5] bg-white shadow-[0px_2px_4px_-2px_rgba(0,0,0,0.10),0px_4px_6px_-1px_rgba(0,0,0,0.10)]">
                <div className="relative border-b border-[#E5E5E5] p-2">
                  <Input
                    type="search"
                    icon={<Search className="size-4 text-[#737373]" />}
                    placeholder={t(
                      "partners.table.filterPopover.categorySearchPlaceholder",
                    )}
                    value={categorySearch}
                    onChange={(event) => setCategorySearch(event.target.value)}
                    className="h-8 border-[#E5E5E5] pr-8 text-sm shadow-none focus-visible:ring-0"
                  />
                  {categorySearch ? (
                    <Button
                      type="button"
                      variant="span"
                      className="absolute top-1/2 right-4 -translate-y-1/2 rounded p-1 text-[#737373] hover:bg-zinc-100 hover:text-[#171717]"
                      onClick={() => setCategorySearch("")}
                      aria-label={t(
                        "partners.table.filterPopover.clearCategorySearchAria",
                      )}
                    >
                      <X className="size-3.5" />
                    </Button>
                  ) : null}
                </div>
                <div className="max-h-[176px] overflow-y-auto p-1">
                  {visibleCategoryOptions.length === 0 ? (
                    <p className="px-2 py-1.5 text-sm text-[#737373]">
                      {t("partners.table.filterPopover.noCategoriesFound")}
                    </p>
                  ) : (
                    <div className="space-y-1">
                      {visibleCategoryOptions.map((category) => {
                        const checked = draft.categories.includes(category);
                        return (
                          <label
                            key={category}
                            className={cn(
                              "flex min-h-8 w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5",
                              checked ? "bg-[#F5F5F5]" : "hover:bg-[#F5F5F5]",
                            )}
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={() => toggleCategory(category)}
                              className={GUESTERIA_FILTER_CHECKBOX_CLASS}
                            />
                            <span className="text-sm leading-normal font-normal text-[#171717]">
                              {t(`partners.table.categoryValues.${category}`)}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </div>

          <div className="space-y-2">
            <Button
              type="button"
              variant="span"
              className={GUESTERIA_FILTER_COMBOBOX_TRIGGER_CLASS}
              onClick={() => setConnectionOpen((v) => !v)}
              aria-expanded={connectionOpen}
            >
              {t("partners.table.filterPopover.connectionStatus")}
              <ChevronDown
                className={cn(
                  "size-4 shrink-0 text-[#737373] transition-transform",
                  connectionOpen && "rotate-180",
                )}
              />
            </Button>

            {connectionOpen ? (
              <div className="overflow-hidden rounded-md border border-[#E5E5E5] bg-white shadow-[0px_2px_4px_-2px_rgba(0,0,0,0.10),0px_4px_6px_-1px_rgba(0,0,0,0.10)]">
                <div className="max-h-[176px] overflow-y-auto p-1">
                  <div className="space-y-1">
                    {GUESTERIA_PARTNER_CONNECTION_OPTIONS.map((status) => {
                      const checked = draft.connectionStatuses.includes(status);
                      return (
                        <label
                          key={status}
                          className={cn(
                            "flex min-h-8 w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5",
                            checked ? "bg-[#F5F5F5]" : "hover:bg-[#F5F5F5]",
                          )}
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={() => toggleConnection(status)}
                            className={GUESTERIA_FILTER_CHECKBOX_CLASS}
                          />
                          <span className="text-sm leading-normal font-normal text-[#171717]">
                            {t(
                              `partners.table.connectionStatusValues.${status}`,
                            )}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            className="h-8 rounded-md border-[#E5E5E5] bg-white px-4 text-sm font-medium text-zinc-900 shadow-none hover:bg-zinc-50"
            onClick={() => {
              setDraft(DEFAULT_GUESTERIA_PARTNERS_FILTERS);
              setCategorySearch("");
              setCategoryOpen(true);
              setConnectionOpen(false);
              onApply(DEFAULT_GUESTERIA_PARTNERS_FILTERS);
            }}
          >
            {t("partners.table.filterPopover.reset")}
          </Button>
          <Button
            type="button"
            className="h-8 rounded-md bg-[#6D82A3] px-4 text-sm leading-normal font-medium text-[#FAFAFA] hover:bg-[#5F7597]"
            onClick={() => {
              onApply(draft);
              setOpen(false);
            }}
          >
            {t("partners.table.filterPopover.apply")}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
